/**
 * QuickCreateButton — Botão "Novo" (Quick Create) do header
 *
 * Duas variantes:
 *  - `launcher` (padrão): ícone quadrado que abre o popover completo com
 *    favoritos + todas as ações.
 *  - `round`: botão redondo exibido ao lado da busca global (estilo Pipedrive),
 *    que abre o menu compacto com as 6 ações principais e seus atalhos;
 *    o rodapé "Ver todas as ações" alterna para o popover completo.
 *
 * RELEASE: 2026-08-06 v1.3.0 - Tooltip informando os atalhos globais (Alt+tecla).
 * RELEASE: 2026-08-06 v1.2.0 - Variante `round` + menu compacto (QuickCreateMenu).

 * RELEASE: 2026-07-24 v1.1.0 - Visual premium: gradiente, borda, shadow, hover elevate.
 * RELEASE: 2026-07-24 v1.0.0 - Criação.
 */
import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { QuickCreatePopover } from './QuickCreatePopover';
import { QuickCreateMenu } from './QuickCreateMenu';
import { useAvailableQuickCreates } from '@/hooks/useAvailableQuickCreates';
import { cn } from '@/lib/utils';

interface QuickCreateButtonProps {
  /** Estilo do gatilho no header. */
  variant?: 'launcher' | 'round';
}

export function QuickCreateButton({ variant = 'launcher' }: QuickCreateButtonProps) {
  const [open, setOpen] = useState(false);
  // v1.2.0: na variante redonda começa no menu compacto e pode expandir.
  const [showAll, setShowAll] = useState(variant === 'launcher');
  const available = useAvailableQuickCreates();

  // Se o usuário não pode acionar nenhuma criação, não polui o header.
  if (available.length === 0) return null;
  // Variante redonda depende das ações principais liberadas.
  if (variant === 'round' && !available.some((a) => a.primary)) return null;

  const isRound = variant === 'round';

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setShowAll(variant === 'launcher');
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'transition-all duration-200 focus-visible:ring-2 focus-visible:ring-emerald-500/50',
                isRound
                  ? 'h-10 w-10 rounded-full border border-border bg-background shadow-sm hover:border-emerald-500/50 hover:bg-emerald-500/5 hover:shadow-md'
                  : 'h-9 w-9 rounded-xl border border-border/70 bg-gradient-to-br from-card to-muted/40 shadow-sm hover:-translate-y-0.5 hover:border-emerald-500/40 hover:shadow-md hover:bg-gradient-to-br hover:from-card hover:to-emerald-500/5 active:translate-y-0 active:shadow-sm',
              )}
              aria-label="Novo (ações rápidas)"
            >
              <Plus className={cn(isRound ? 'h-5 w-5 text-foreground' : 'h-4 w-4 text-emerald-500')} />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          Novo — atalhos Alt+tecla funcionam em qualquer tela
        </TooltipContent>

      </Tooltip>
      <PopoverContent
        align="end"
        sideOffset={8}
        className={cn(
          'max-h-[85vh] overflow-hidden rounded-2xl border-border/70 p-0 shadow-xl',
          showAll ? 'w-auto' : 'w-[260px]',
        )}
      >
        {showAll ? (
          <QuickCreatePopover onClose={() => handleOpenChange(false)} />
        ) : (
          <QuickCreateMenu
            onClose={() => handleOpenChange(false)}
            onOpenAll={() => setShowAll(true)}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
